import { Eye, EyeClosed, Key, Mail, Phone } from "lucide-react";
import React, { useEffect, useState } from "react";
import { auth, provider, db } from "../utils/firebase";
import {
    signInWithEmailAndPassword,
    signOut,
    signInWithPhoneNumber,
    RecaptchaVerifier,
    signInWithRedirect,
    getRedirectResult,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { LANDING_PATH, SIGN_UP_PATH, FIGHTER_REGISTER_PATH, ADMIN_PATH } from "../App";
import { getRedirectIntent, clearRedirectIntent } from "../utils/redirect-intent";
import { Logo } from "../components/logo";
import SelectInput from "../components/select-input";
import { countryDialCodes } from "../constants";
import { useTranslation } from "react-i18next";

export default function Login() {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [selectedDialCode, setSelectedDialCode] = useState("+60");
    const [otp, setOtp] = useState("");
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [passwordVisibility, setPasswordVisibility] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [authMethod, setAuthMethod] = useState("email"); // 'email' or 'phone'
    const navigate = useNavigate();
    const redirectIntent = getRedirectIntent();

    const handleLogin = async () => {
        setIsLoading(true);
        try {
            if (authMethod === "email") {
                if (email && password) {
                    const userCredential = await signInWithEmailAndPassword(
                        auth,
                        email?.trim(),
                        password?.trim()
                    );

                    if (!userCredential.user.emailVerified) {
                        toast.error(
                            "Please check the mailbox to verify the email before continue login."
                        );
                        signOut(auth);
                        return;
                    } else {
                        toast.success("Login Successful");
                        // Wait a moment for auth context to update, then check fighter status
                        setTimeout(async () => {
                            const userRef = doc(
                                db,
                                "users",
                                userCredential.user.uid
                            );
                            const userSnap = await getDoc(userRef);
                            const fighterRef = doc(
                                db,
                                "fighters",
                                userCredential.user.uid
                            );
                            const fighterSnap = await getDoc(fighterRef);

                            const userData = userSnap.exists() ? userSnap.data() : null;
                            const isFighter = fighterSnap.exists() && userData?.role === "fighter";

                            if (userData?.role === "admin") {
                                // Admin - redirect to admin dashboard
                                clearRedirectIntent();
                                navigate(ADMIN_PATH, { replace: true });
                            } else if (userData && userData.role !== "admin") {
                                if (!isFighter) {
                                    // Not a fighter - redirect to fighter registration (intent preserved in localStorage)
                                    navigate(FIGHTER_REGISTER_PATH, { replace: true });
                                } else {
                                    // Is a fighter - redirect to original intent or homepage
                                    clearRedirectIntent();
                                    navigate(redirectIntent || LANDING_PATH, { replace: true });
                                }
                            } else {
                                // No user data - redirect to homepage
                                clearRedirectIntent();
                                navigate(LANDING_PATH, { replace: true });
                            }
                        }, 500);
                    }
                } else {
                    toast.error("Please fill up the form.");
                }
            } else {
                if (phoneNumber) {
                    if (!showOtpInput) {
                        const appVerifier = new RecaptchaVerifier(
                            auth,
                            "recaptcha-container",
                            {
                                size: "invisible",
                                callback: async () => {
                                    console.log(
                                        "Recaptcha verification successful."
                                    );
                                },
                                "error-callback": () => {
                                    console.log(
                                        "Recaptcha verification failed."
                                    );
                                    toast.error(
                                        "Recaptcha verification failed."
                                    );
                                },
                                "expired-callback": () => {
                                    console.log(
                                        "Recaptcha verification expired."
                                    );
                                    toast.error(
                                        "Recaptcha verification expired."
                                    );
                                },
                                "reset-callback": () => {
                                    console.log(
                                        "Recaptcha verification reset."
                                    );
                                    toast.error(
                                        "Recaptcha verification reset."
                                    );
                                },
                            }
                        );

                        let formattedPhoneNumber = phoneNumber;
                        if (
                            selectedDialCode === "+60" &&
                            phoneNumber.startsWith("0")
                        ) {
                            formattedPhoneNumber = phoneNumber.substring(1);
                        }
                        const fullPhoneNumber = `${selectedDialCode}${formattedPhoneNumber}`;
                        const confirmationResult = await signInWithPhoneNumber(
                            auth,
                            fullPhoneNumber,
                            appVerifier
                        );
                        window.confirmationResult = confirmationResult;
                        setShowOtpInput(true);
                        toast.success("OTP sent to your phone");
                    } else {
                        const confirmationResult = window.confirmationResult;
                        const result = await confirmationResult.confirm(otp);
                        toast.success("Login Successful");
                        // Wait a moment for auth context to update, then check fighter status
                        setTimeout(async () => {
                            const userRef = doc(db, "users", result.user.uid);
                            const userSnap = await getDoc(userRef);
                            const fighterRef = doc(
                                db,
                                "fighters",
                                result.user.uid
                            );
                            const fighterSnap = await getDoc(fighterRef);

                            const userData = userSnap.exists() ? userSnap.data() : null;
                            const isFighter = fighterSnap.exists() && userData?.role === "fighter";

                            if (userData?.role === "admin") {
                                // Admin - redirect to admin dashboard
                                clearRedirectIntent();
                                navigate(ADMIN_PATH, { replace: true });
                            } else if (userData && userData.role !== "admin") {
                                if (!isFighter) {
                                    // Not a fighter - redirect to fighter registration (intent preserved in localStorage)
                                    navigate(FIGHTER_REGISTER_PATH, { replace: true });
                                } else {
                                    // Is a fighter - redirect to original intent or homepage
                                    clearRedirectIntent();
                                    navigate(redirectIntent || LANDING_PATH, { replace: true });
                                }
                            } else {
                                // No user data - redirect to homepage
                                clearRedirectIntent();
                                navigate(LANDING_PATH, { replace: true });
                            }
                        }, 500);
                    }
                } else {
                    toast.error("Please enter your phone number");
                }
            }
        } catch (error) {
            console.error(error);
            toast.error(`Login Failed`);
        } finally {
            setIsLoading(false);
        }
    };

    const tooglePasswordVisibility = () => {
        setPasswordVisibility((prev) => !prev);
    };

    const handleSignInWithGoogle = async () => {
        try {
            await signInWithRedirect(auth, provider);
        } catch (error) {
            console.error(`Error on login by Google: ${error}`);
            toast.error("Login Faild");
        }
    };

    const toggleAuthMethod = () => {
        setAuthMethod((prev) => (prev === "email" ? "phone" : "email"));
        setShowOtpInput(false);
        setOtp("");
    };

    const dialCodeOptions = countryDialCodes.map(
        (item) => `${item.code} (${item.country})`
    );

    useEffect(() => {
        (async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result?.user) {
                    toast.success("Login Successful");
                    // Wait a moment for auth context to update, then check fighter status
                    setTimeout(async () => {
                        const userRef = doc(db, "users", result.user.uid);
                        const userSnap = await getDoc(userRef);
                        const fighterRef = doc(db, "fighters", result.user.uid);
                        const fighterSnap = await getDoc(fighterRef);

                        const userData = userSnap.exists() ? userSnap.data() : null;
                        const isFighter = fighterSnap.exists() && userData?.role === "fighter";

                        if (userData?.role === "admin") {
                            // Admin - redirect to admin dashboard
                            clearRedirectIntent();
                            navigate(ADMIN_PATH, { replace: true });
                        } else if (userData && userData.role !== "admin") {
                            if (!isFighter) {
                                // Not a fighter - redirect to fighter registration (intent preserved in localStorage)
                                navigate(FIGHTER_REGISTER_PATH, { replace: true });
                            } else {
                                // Is a fighter - redirect to original intent or homepage
                                clearRedirectIntent();
                                navigate(redirectIntent || LANDING_PATH, { replace: true });
                            }
                        } else {
                            // No user data - redirect to homepage
                            clearRedirectIntent();
                            navigate(LANDING_PATH, { replace: true });
                        }
                    }, 500);
                }
            } catch (error) {
                console.error(error);
            }
        })();
    }, [auth, navigate, redirectIntent]);

    return (
        <div className="w-full min-h-[calc(100vh-6.5rem)] flex-center p-4">
            <div className="flex-center gap-4 w-full max-w-[24rem] p-4">
                <Link to={"/"}>
                    <Logo />
                </Link>

                {authMethod === "email" ? (
                    <>
                        <label className="input input-bordered flex items-center gap-2 w-full">
                            <Mail />
                            <input
                                type="text"
                                className="pl-1 grow"
                                placeholder={t("auth.email")}
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                }}
                            />
                        </label>
                        <label className="input input-bordered flex items-center gap-2 w-full relative">
                            <Key />
                            <input
                                type={passwordVisibility ? "text" : "password"}
                                className="w-full pl-1 grow"
                                placeholder={t("auth.password")}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                }}
                            />
                            <button
                                className="btn btn-square btn-ghost absolute top-0 right-0"
                                onClick={tooglePasswordVisibility}
                            >
                                {passwordVisibility ? <Eye /> : <EyeClosed />}
                            </button>
                        </label>
                    </>
                ) : (
                    <>
                        <div className="flex gap-2 w-full">
                            <div className="w-1/3">
                                <SelectInput
                                    options={dialCodeOptions}
                                    defaultValue={selectedDialCode}
                                    value={selectedDialCode}
                                    onSelect={(value) => {
                                        setSelectedDialCode(
                                            value.split(" ")[0]
                                        );
                                    }}
                                    placeholder="+60"
                                />
                            </div>
                            <label className="input input-bordered flex items-center gap-2 w-2/3">
                                <Phone />
                                <input
                                    type="tel"
                                    className="pl-1 grow"
                                    placeholder={t("auth.phoneNumber")}
                                    value={phoneNumber}
                                    onChange={(e) => {
                                        setPhoneNumber(e.target.value);
                                    }}
                                />
                            </label>
                        </div>
                        {showOtpInput && (
                            <label className="input input-bordered flex items-center gap-2 w-full">
                                <Key />
                                <input
                                    type="text"
                                    className="pl-1 grow"
                                    placeholder={t("auth.enterOtp")}
                                    inputMode="numeric"
                                    value={otp}
                                    onChange={(e) => {
                                        setOtp(e.target.value);
                                    }}
                                />
                            </label>
                        )}
                        <div id="recaptcha-container"></div>
                    </>
                )}

                <div className="self-end">
                    {authMethod === "email" && (
                        <button
                            className="btn btn-sm btn-ghost underline"
                            onClick={() => navigate("/forget-password")}
                        >
                            {t("auth.forgotPassword")}
                        </button>
                    )}
                </div>
                <button
                    onClick={handleLogin}
                    className="w-full btn btn-primary"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <span className="loading loading-spinner" />
                    ) : authMethod === "email" ? (
                        t("auth.login")
                    ) : showOtpInput ? (
                        t("auth.verifyOtp")
                    ) : (
                        t("auth.sendOtp")
                    )}
                </button>
                <p className="flex justify-center items-center gap-2">
                    {t("auth.noAccount")}
                    <Link
                        to={SIGN_UP_PATH}
                        className="btn btn-ghost btn-sm underline"
                    >
                        {t("common.signup")}
                    </Link>
                </p>
                <div className="divider">{t("auth.or")}</div>
                <button
                    className="btn btn-outline w-full flex flex-row gap-2 justify-center items-center "
                    onClick={handleSignInWithGoogle}
                >
                    <img
                        src="/assets/img/logo-google.svg"
                        className="h-6"
                        alt="gooogle icon"
                    />
                    <p>{t("auth.signInWithGoogle")}</p>
                </button>
                <button
                    className="btn btn-ghost w-full"
                    onClick={toggleAuthMethod}
                >
                    {authMethod === "email" ? <Phone /> : <Mail />}
                    {authMethod === "email"
                        ? t("auth.signInWithPhone")
                        : t("auth.signInWithEmail")}
                </button>
            </div>
        </div>
    );
}
